import { Link } from 'react-router';

/**
 * Layout for the blog, which is a separate build mounted at /blog under the
 * static marketing site on playanoedu.com.
 *
 * This router's basename is /blog, so `Link to="/"` resolves to /blog/ — the
 * page the reader is already on. The logo and every site nav item must therefore
 * be plain anchors to the marketing site, which is a different document. Only
 * the blog hub is a router Link.
 */

const SITE = 'https://playanoedu.com';

const SITE_LINKS = [
  { label: 'Products', href: `${SITE}/#products` },
  { label: 'Method', href: `${SITE}/#ai-native` },
  { label: 'About', href: `${SITE}/#about` },
  { label: 'Contact', href: `${SITE}/#contact` },
];

const LEGAL = [
  ['Privacy', 'privacy_policy.html'],
  ['Terms', 'terms_of_service.html'],
  ['Cookies', 'cookie-policy.html'],
  ['DMCA', 'dmca.html'],
  ['Accessibility', 'accessibility.html'],
];

export const BlogLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-[#111111] text-gray-200">
    <header className="border-b border-[#333]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <a href={`${SITE}/`} className="flex items-center gap-2 font-semibold text-white shrink-0">
          <img src={`${SITE}/256x256Playano.png`} alt="Playano Education" className="w-7 h-7" />
          <span className="hidden sm:inline">Playano Education</span>
        </a>

        <nav className="flex items-center gap-5 sm:gap-6">
          <div className="hidden md:flex items-center gap-6">
            {SITE_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>
          <Link
            to="/"
            className="text-sm font-medium text-[#FD9800] hover:text-white transition-colors"
          >
            Blog
          </Link>
        </nav>
      </div>
    </header>

    <main>{children}</main>

    <footer className="border-t border-[#333] mt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid gap-8 sm:grid-cols-3">
          <div>
            <p className="text-sm font-semibold text-white mb-3">Playano Education</p>
            <p className="text-sm text-gray-500">
              An AI-native product studio building for learning, entertainment, and workforce
              automation.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-white mb-3">Site</p>
            <ul className="space-y-2 text-sm">
              <li>
                <a href={`${SITE}/`} className="text-gray-500 hover:text-[#FD9800]">
                  Home
                </a>
              </li>
              {SITE_LINKS.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="text-gray-500 hover:text-[#FD9800]">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-white mb-3">Legal</p>
            <ul className="space-y-2 text-sm">
              {LEGAL.map(([label, path]) => (
                <li key={path}>
                  <a href={`${SITE}/${path}`} className="text-gray-500 hover:text-[#FD9800]">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-8 pt-6 border-t border-[#222] text-xs text-gray-600">
          &copy; {new Date().getFullYear()} Playano Education Limited.
        </p>
      </div>
    </footer>
  </div>
);
